import { Navigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { satisfiesRoleRequirement } from '@/lib/dashboardAccess'
import { customerLoginPathWithShop } from '@/lib/customerShopReturn'
import { hasRecentStaffLogoutIntent } from '@/lib/staffLoginRedirect'

interface ProtectedRouteProps {
  children: React.ReactNode
  requireAuth?: boolean
  requireStaff?: boolean
  requireCustomer?: boolean
  requireRole?: string[]
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAuth = true,
  requireStaff = false,
  requireCustomer = false,
  requireRole = [],
}) => {
  const { isAuthenticated, user, isLoading } = useAuthStore()
  const location = useLocation()

  // Debug logging
  if (requireCustomer) {
    console.log('ProtectedRoute (Customer):', {
      isAuthenticated,
      user,
      isLoading,
      userIsWebUser: user?.is_web_user,
      pathname: location.pathname,
    })
  }

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  // Check if authentication is required
  if (requireAuth && !isAuthenticated) {
    // Redirect to appropriate login page based on route requirement
    const loginPath = requireCustomer
      ? customerLoginPathWithShop({
          redirect: `${location.pathname}${location.search || ''}`,
        })
      : '/auth/staff/login'
    const preserveReturn =
      !requireCustomer && !hasRecentStaffLogoutIntent()
    return (
      <Navigate
        to={loginPath}
        state={preserveReturn ? { from: location } : undefined}
        replace
      />
    )
  }

  // Check staff requirement
  if (requireStaff && (!user || user.is_web_user)) {
    const preserveReturn = !hasRecentStaffLogoutIntent()
    return (
      <Navigate
        to="/auth/staff/login"
        state={preserveReturn ? { from: location } : undefined}
        replace
      />
    )
  }

  // Check customer requirement
  if (requireCustomer && (!user || !user.is_web_user)) {
    console.log('ProtectedRoute: Customer check failed', {
      user,
      isWebUser: user?.is_web_user,
    })
    return (
      <Navigate
        to={customerLoginPathWithShop({
          redirect: `${location.pathname}${location.search || ''}`,
        })}
        state={{ from: location }}
        replace
      />
    )
  }

  // Check role requirement
  if (requireRole.length > 0 && !satisfiesRoleRequirement(user?.role, requireRole)) {
    return <Navigate to="/unauthorized" replace state={{ from: location.pathname }} />
  }

  return <>{children}</>
}

export default ProtectedRoute
